class Table {
  #tableElement = null;
  #draggableTable = null;
  #printButton = null;
  #fullscreenButton = null;
  #closeFullscreenButton = null;
  #zoomInButton = null;
  #zoomOutButton = null;
  #savePdfButton = null;
  #tableFormElement = null;
  #fontStep = null;
  #fontSize = 16;
  #isDown = false;
  #startX = null;
  #scrollLeft = null;
  #scrollSpeed = null;
  #tableType = 'default';
  #scrollTop = null;
  #isRnActions = null;
  #index = null;

  constructor(tableElement, index, scrollSpeed = 1.5, fontStep = 2) {
    this.#tableElement = tableElement;
    this.#scrollSpeed = scrollSpeed;
    this.#fontStep = fontStep;
    this.#draggableTable = this.#tableElement;
    this.#index = index;
  }

  init() {
    this.#handleClasses();
    if (this.#tableType === 'default') {
      return;
    } else if (this.#tableType === 'combine' || this.#tableType === 'fullscreen') {
      this.#printButton = this.#tableElement.previousSibling.previousSibling.querySelector('[data-table-print]');
      this.#fullscreenButton = this.#tableElement.previousSibling.previousSibling.querySelector('[data-table-fullscreen]');
      this.#closeFullscreenButton = this.#tableElement.previousSibling.previousSibling.querySelector('[data-table-close]');
      this.#zoomInButton = this.#tableElement.previousSibling.previousSibling.querySelector('[data-table-zoom-in]');
      this.#zoomOutButton = this.#tableElement.previousSibling.previousSibling.querySelector('[data-table-zoom-out]');
      this.#savePdfButton = this.#tableElement.previousSibling.previousSibling.querySelector('[data-table-download]');
      this.#tableFormElement = this.#tableElement.previousSibling;
    } else {
      this.#printButton = this.#tableElement.previousSibling.querySelector('[data-table-print]');
      this.#fullscreenButton = this.#tableElement.previousSibling.querySelector('[data-table-fullscreen]');
      this.#closeFullscreenButton = this.#tableElement.previousSibling.querySelector('[data-table-close]');
      this.#zoomInButton = this.#tableElement.previousSibling.querySelector('[data-table-zoom-in]');
      this.#zoomOutButton = this.#tableElement.previousSibling.querySelector('[data-table-zoom-out]');
      this.#savePdfButton = this.#tableElement.previousSibling.querySelector('[data-table-download]');
      this.#tableFormElement = this.#tableElement.previousSibling;
    }
    this.#handleEventListeners();
  }

  #handleEventListeners = () => {
    this.#onTableDrag();
    this.#onPrintButtonClick();
    if (this.#fullscreenButton) {
      this.#fullscreenButton.addEventListener('click', this.#onFullscreenButtonClick);
    }
    if (this.#closeFullscreenButton) {
      this.#closeFullscreenButton.addEventListener('click', this.#onCloseFullscreenButtonClick);
    }
    if (this.#zoomInButton) {
      this.#zoomInButton.addEventListener('click', () => this.#onChangeTableScale('plus'));
    }
    if (this.#zoomOutButton) {
      this.#zoomOutButton.addEventListener('click', () => this.#onChangeTableScale('minus'));
    }
    if (this.#savePdfButton) {
      this.#savePdfButton.addEventListener('click', this.#onSavePdfButtonClick);
    }
  };

  #onTableDrag = () => {
    this.#draggableTable.addEventListener('mousedown', (e) => {
      this.#isDown = true;
      this.#draggableTable.classList.add('active');
      this.#startX = e.pageX - this.#draggableTable.offsetLeft;
      this.#scrollLeft = this.#draggableTable.scrollLeft;
    });

    this.#draggableTable.addEventListener('mouseleave', () => {
      this.#isDown = false;
      this.#draggableTable.classList.remove('active');
    });

    this.#draggableTable.addEventListener('mouseup', () => {
      this.#isDown = false;
      this.#draggableTable.classList.remove('active');
    });

    this.#draggableTable.addEventListener('mousemove', (e) => {
      if (!this.#isDown) {
        return;
      }
      e.preventDefault();
      const x = e.pageX - this.#draggableTable.offsetLeft;
      const walk = (x - this.#startX) * this.#scrollSpeed;
      this.#draggableTable.scrollLeft = this.#scrollLeft - walk;
    });
  };

  #onPrintButtonClick = () => {
    if (this.#printButton) {
      this.#printButton.addEventListener('click', this.#printTable);
    }
  };

  #printTable = () => {
    const printContents = this.#tableElement.querySelector('table').outerHTML;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Table</title>
          <style>
            table {
              border-collapse: collapse;
              width: 100%;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 8px;
              text-align: left;
            }
            th p, td p {
              margin: 0;
            }
          </style>
        </head>
        <body onload="window.close();">${printContents}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  #onFullscreenButtonClick = () => {
    this.#tableElement.classList.add('table-wrapper--full-screen');
    this.#tableElement.previousSibling.previousSibling.classList.add('table-wrapper__actions--full-screen');
    this.#disabledScroll();
  };

  #onCloseFullscreenButtonClick = () => {
    this.#tableElement.classList.remove('table-wrapper--full-screen');
    this.#tableElement.previousSibling.previousSibling.classList.remove('table-wrapper__actions--full-screen');
    this.#resetFontSize();
    this.#enableScroll();
  };

  #disabledScroll() {
    if (parseInt(document.body.style.top, 10)) {
      this.#scrollTop = parseInt(document.body.style.top, 10);
    } else {
      this.#scrollTop = self.pageYOffset || (document.documentElement && document.documentElement.ScrollTop) || (document.body && document.body.scrollTop);
    }

    const scrollWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollWidth) {
      document.body.style.paddingRight = `${scrollWidth}px`;
    }
    document.body.style.top = `-${this.#scrollTop}px`;
    document.querySelector('.main').style.height = '100vh';
    document.body.classList.add('scroll-lock');
  }

  #enableScroll() {
    this.#scrollTop = parseInt(document.body.style.top, 10);
    document.body.classList.remove('scroll-lock');
    document.querySelector('.main').style.height = null;
    document.body.style.top = null;
    document.body.style.paddingRight = 0;
    window.scrollTo({
      top: this.#scrollTop * -1,
      left: 0,
      behavior: 'instant',
    });
    this.#scrollTop = null;
  }

  #onChangeTableScale = (sign) => {
    this.#fontSize += (sign === 'plus' ? this.#fontStep : -this.#fontStep);
    this.#tableElement.querySelectorAll('thead th, thead td, tbody td').forEach((element) => {
      element.style.fontSize = `${this.#fontSize}px`;
    });
  };

  #resetFontSize = () => {
    this.#tableElement.querySelectorAll('thead th, thead td, tbody td').forEach((element) => {
      element.style.fontSize = null;
    });
    this.#fontSize = 16;
  };

  #onSavePdfButtonClick = async () => {
    this.#toggleLoader();
    try {
      let pdfUrlResponse = await fetch(`/bitrix/services/main/ajax.php?c=composer:pdf.table&action=get&mode=class&page=${window.location.pathname}&index=${this.#index}`);

      if (!pdfUrlResponse.ok) {
        throw new Error('Ошибка сети: ' + pdfUrlResponse.status);
      }

      let responseData = await pdfUrlResponse.json();
      if (responseData.status !== 'success') {
        throw new Error('Ошибка получения PDF: ' + JSON.stringify(responseData.errors));
      }
      let pdf_url = responseData.data;
      let link = document.createElement("a");
      link.setAttribute('download', "RANEPA_REPOSITORY.pdf");
      link.href = pdf_url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      console.log("Ошибка HTTP: " + pdfUrlResponse.status);
    } finally {
      this.#toggleLoader();
    }
  };

  #handleClasses = () => {
    if (this.#tableElement.classList.contains('table-wrapper-print') && this.#tableElement.classList.contains('table-wrapper-full')) {
      this.#tableType = 'combine';
    }

    if (this.#tableElement.classList.contains('table-wrapper-full') && !this.#tableElement.classList.contains('table-wrapper-print')) {
      this.#tableType = 'fullscreen';
    }

    if (!this.#tableElement.classList.contains('table-wrapper-full') && this.#tableElement.classList.contains('table-wrapper-print')) {
      this.#tableType = 'print';
    }

    this.#handleTableType();
  };

  #handleTableType = () => {
    if (this.#tableType === 'default') {
      return;
    }

    if (this.#tableType !== 'default') {
      const template = document.createElement('div');
      template.classList.add('table-wrapper__actions');
      template.innerHTML = `
        <div class="table-wrapper__default-buttons">
          ${(this.#tableType === 'combine' || this.#tableType === 'fullscreen') ? this.#createFullScreenButton() : ''}
          ${(this.#tableType === 'combine' || this.#tableType === 'print') ? this.#createPrintButtons() : ''}
        </div>
        ${(this.#tableType === 'combine' || this.#tableType === 'fullscreen') ? this.#createFullscreeenModeControls() : ''}
      `;

      this.#tableElement.parentElement.insertBefore(template, this.#tableElement);

    }

    if (this.#tableType !== 'print') {
      this.#tableElement.parentElement.insertBefore(this.#createFormElement(), this.#tableElement);
    }
  };

  #createPrintButtons = () => (`
    <div class="table-wrapper__action-buttons">
      <button class="table-wrapper__button table-wrapper__button--pdf" data-table-download>
        <span class="visually-hidden">Скачать PDF таблицу</span>
      </button>
      <button class="table-wrapper__button table-wrapper__button--print" data-table-print>
        <span class="visually-hidden">Распечать страницу</span>
      </button>
    </div>
  `);

  #createFormElement = () => {
    const form = document.createElement('form');
    form.className = 'table-wrapper__form visually-hidden';
    form.target = 'mywin';
    form.name = 'myform';
    form.method = 'post';
    form.action = 'https://www.ranepa.ru/pdf_table.php';
    form.enctype = 'multipart/form-data';
    form.setAttribute('data-table-form', '');

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'em';

    const textarea = document.createElement('textarea');
    textarea.name = 'pdfstr[]';

    form.appendChild(hiddenInput);
    form.appendChild(textarea);
    return form;
  };

  #createFullScreenButton = () => {
    this.isRnActions = this.#tableElement.parentElement.classList.contains('rn-actions-table');

    return this.isRnActions ? (`
      <button class="r-button r-button--main table-wrapper__fullscreen-mode" type="button" data-table-fullscreen>
        <span class="visually-hidden">Развернуть во весь экран</span>
      </button>`) : (`
      <button class="r-button r-button--main table-wrapper__fullscreen-mode" type="button" data-table-fullscreen>
        Во весь экран
        <div class="r-button__gradient"></div>
      </button>`);
  };

  #createFullscreeenModeControls = () => (`
    <div class="table-wrapper__modal-buttons">
      <button class="table-wrapper__button table-wrapper__button--zoom-plus" data-table-zoom-in>
        <span class="visually-hidden">Увеличить текст</span>
      </button>
      <button class="table-wrapper__button table-wrapper__button--zoom-minus" data-table-zoom-out>
        <span class="visually-hidden">Уменьшить текст</span>
      </button>
      <button class="table-wrapper__button table-wrapper__button--close"  data-table-close>
        <span class="visually-hidden">закрыть режим во весь экран</span>
      </button>
    </div>
  `);

  #toggleLoader = () => {
    const loader = document.querySelector('.loader');
    if (loader) {
      loader.classList.toggle('loader--show');
    }
  }
}

const initTables = () => {
  const tables = document.querySelectorAll('.table-wrapper');
  if (tables.length) {
    tables.forEach((table, index) => {
      const newTable = new Table(table, index);
      newTable.init();
    });
  }
};

export { initTables };
